import React, { useState, useEffect } from 'react';
import {
  metricTileCardBase,
  metricTileDescription,
  metricTileInner,
  metricTileStandardCard,
  metricTileTitle,
} from '@/components/metrics/metricTileStyles';
import { MetricTileValue } from '@/components/metrics/MetricTileValue';
import { TrendInfo } from '@/queries/retail';

interface MetricWithTrendProps {
  metric: {
    name: string;
    value: number;
    description: string;
    metricKey?: string;
  };
  trend?: TrendInfo;
  onClick?: () => void;
}

// Reference period map
const REFERENCE_PERIODS: Record<string, string> = {
  'hour_flights': 'last 60 mins',
  'hour_pax': 'last 60 mins',
  'current_pax': 'last 10 mins',
};

// Color logic reflects commercial opportunity
export const getTrendColor = (trendState: string): string => {
  const colors = {
    BUILDING_QUICKLY: 'from-green-500/15 to-green-600/15 border-green-500/25',
    BUILDING: 'from-green-400/12 to-green-500/12 border-green-400/20',
    FLATTENING: 'from-slate-500/10 to-slate-600/10 border-slate-400/15',
    DRAINING: 'from-blue-500/15 to-blue-600/15 border-blue-500/25',
    DRAINING_QUICKLY: 'from-blue-600/15 to-blue-700/15 border-blue-600/25'
  };
  return colors[trendState] || colors.FLATTENING;
};

// Smaller, more refined pill styling
export const getTrendBadgeClasses = (trendState: string): string => {
  const badges = {
    BUILDING_QUICKLY: 'bg-green-600/35 text-green-100 ring-green-500/40',
    BUILDING: 'bg-green-500/30 text-green-100 ring-green-400/35',
    FLATTENING: 'bg-slate-500/25 text-slate-200 ring-slate-400/30',
    DRAINING: 'bg-blue-500/30 text-blue-100 ring-blue-400/35',
    DRAINING_QUICKLY: 'bg-blue-600/35 text-blue-100 ring-blue-500/40'
  };
  return badges[trendState] || badges.FLATTENING;
};

export const getTrendArrow = (trendState: string): string => {
  if (trendState.includes('BUILDING')) return '↑';
  if (trendState.includes('DRAINING')) return '↓';
  return '→';
};

export const getTrendLabel = (trendState: string): string => {
  const labels = {
    BUILDING_QUICKLY: 'Building quickly',
    BUILDING: 'Building',
    FLATTENING: 'Flattening',
    DRAINING: 'Draining',
    DRAINING_QUICKLY: 'Draining quickly'
  };
  return labels[trendState] || 'Flattening';
};

export const MetricTileWithTrend: React.FC<MetricWithTrendProps> = ({
  metric,
  trend,
  onClick
}) => {
  const [isPulsing, setIsPulsing] = useState(true);
  const displayValue = metric.value ?? 0;
  const trendColor = trend ? getTrendColor(trend.trend_state) : '';
  const badgeClasses = trend ? getTrendBadgeClasses(trend.trend_state) : '';
  const arrow = trend ? getTrendArrow(trend.trend_state) : '';
  const trendLabel = trend ? getTrendLabel(trend.trend_state) : '';
  
  // Get reference period
  const referencePeriod = metric.metricKey 
    ? REFERENCE_PERIODS[metric.metricKey] || 'reference period'
    : 'reference period';

  // Determine if should pulse initially (critical states: BUILDING_QUICKLY, DRAINING_QUICKLY)
  const shouldPulseInitially = trend && trend.trend_state.includes('QUICKLY');

  // Stop pulsing after a short attention window, including when store data changes
  useEffect(() => {
    if (!shouldPulseInitially) {
      setIsPulsing(false);
      return;
    }

    setIsPulsing(true);
    const timer = window.setTimeout(() => {
      setIsPulsing(false);
    }, 10000);

    return () => window.clearTimeout(timer);
  }, [shouldPulseInitially, trend?.trend_state, trend?.current_value, trend?.absolute_change, metric.metricKey]);

  return (
    <div
      className={`${metricTileCardBase} ${metricTileStandardCard} ${
        trend ? `bg-gradient-to-b ${trendColor}` : ''
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${metric.name}: ${displayValue.toLocaleString()}${trend ? `, ${trendLabel} ${trend.percent_change > 0 ? '+' : ''}${trend.percent_change}%` : ''}`}
    >
      <div className={metricTileInner}>
        <div className={metricTileTitle}>{metric.name}</div>
        <MetricTileValue value={displayValue} />
        <div className={metricTileDescription}>{metric.description}</div>

        {/* Detail line with proper reference period */}
        {trend && (
          <div className="text-slate-400 text-[11px] sm:text-xs mt-2.5 mb-3 text-center font-medium">
            {trend.absolute_change > 0 ? '+' : ''}{trend.absolute_change}{' '}
            {metric.name.includes('Passengers') ? 'passengers' : 'flights'} vs {referencePeriod}
          </div>
        )}

        {/* Trend pill */}
        {trend && (
          <div
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${badgeClasses} ${
              isPulsing ? 'animate-pulse' : ''
            }`}
          >
            <span className="text-sm leading-none">{arrow}</span>
            <span className="text-xs font-semibold truncate">{trendLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
};